/**
 * Доступ к карточкам препаратов.
 *
 * Запрос идёт по индексу `userId`, а не по составному `[userId+isActive]`:
 * IndexedDB не умеет индексировать булевы значения, такой ключ просто не
 * попадёт в индекс. Препаратов у человека единицы, поэтому отбор активных в
 * памяти ничего не стоит.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/shared/db/database';
import type { Medication } from '@/shared/db/schema';
import type { IsoDate } from '@/shared/lib/datetime';
import { getCurrentUserId } from '@/shared/db/session-context';
import type { CreateInput, UpdateInput } from '@/shared/db/repository';

import { medicationsRepo } from './repository';

export type MedicationInput = CreateInput<Medication>;

async function queryMedications(): Promise<Medication[]> {
  const userId = getCurrentUserId();
  if (!userId) return [];

  const records = await db.medications.where('userId').equals(userId).toArray();

  return records
    .filter((medication) => medication.deletedAt === null)
    .sort((a, b) => {
      // Активные курсы выше архива, внутри группы — по алфавиту.
      const activeDiff = Number(b.isActive) - Number(a.isActive);
      if (activeDiff !== 0) return activeDiff;
      return a.name.localeCompare(b.name, 'ru');
    });
}

export function useMedications(): Medication[] | undefined {
  return useLiveQuery(queryMedications, []);
}

export function useMedication(id: string | null): Medication | null | undefined {
  return useLiveQuery(async () => {
    if (!id) return null;
    return (await medicationsRepo.get(id)) ?? null;
  }, [id]);
}

export async function createMedication(input: MedicationInput): Promise<Medication> {
  return medicationsRepo.create(input);
}

export async function updateMedication(id: string, patch: UpdateInput<Medication>): Promise<void> {
  await medicationsRepo.update(id, patch);
}

/**
 * Архивирование вместо удаления: курс закончился, но журнал приёмов остаётся
 * доступным — это медицинская история, а не мусор.
 */
export async function archiveMedication(id: string): Promise<void> {
  await medicationsRepo.update(id, { isActive: false, archivedAt: Date.now() });
}

export async function unarchiveMedication(id: string): Promise<void> {
  await medicationsRepo.update(id, { isActive: true, archivedAt: null });
}

export async function deleteMedication(id: string): Promise<void> {
  await medicationsRepo.remove(id);
}

/**
 * Возвращает удалённый препарат.
 *
 * Отдельная функция нужна потому, что обычная правка полей признак удаления не
 * снимает: `update` пишет то, что ему передали, и запись остаётся удалённой.
 */
export async function restoreMedication(id: string): Promise<void> {
  await medicationsRepo.restore(id);
}

/**
 * Списание и пополнение остатка упаковки.
 *
 * Остаток перечитывается внутри транзакции, а не берётся из переданной записи:
 * та приходит из подписки и может отставать. Два быстрых «Принял» по значению
 * из пропсов списывали одну таблетку вместо двух — классическая потеря
 * обновления, и заметить её можно было только по разошедшемуся остатку.
 */
export interface RefillInput {
  /** Сколько купили — прибавится к остатку. */
  amount: number;
  /** Срок годности купленной упаковки. `null` — не указан. */
  expiryDate: IsoDate | null;
}

/**
 * Купленная упаковка: плюс к остатку и новый срок годности.
 *
 * Срок обновляется вместе с остатком, потому что иначе аптечка годами
 * показывает дату давно выброшенной пачки — сначала «просрочено» на том, что
 * куплено вчера, потом молчание о том, что действительно испортилось.
 *
 * Купили — значит, пачка новая и стоит запечатанной: отметка о вскрытии
 * снимается. Иначе «28 дней после вскрытия» продолжали бы считаться от
 * прошлого флакона, и свежая упаковка объявлялась бы просроченной. Сам срок
 * после вскрытия остаётся — он написан на упаковке и у новой пачки тот же.
 */
export async function refillStock(medication: Medication, input: RefillInput): Promise<void> {
  await db.transaction('rw', db.medications, db.outbox, async () => {
    const current = await db.medications.get(medication.id);
    if (!current) return;

    const stock = Math.max(0, Math.round(((current.stockQuantity ?? 0) + input.amount) * 100) / 100);

    await medicationsRepo.update(medication.id, {
      stockQuantity: stock,
      expiryDate: input.expiryDate,
      openedAt: null,
    });
  });
}

export async function adjustStock(medication: Medication, delta: number): Promise<void> {
  if (medication.stockQuantity === null) return;

  await db.transaction('rw', db.medications, db.outbox, async () => {
    const current = await db.medications.get(medication.id);
    if (!current || current.stockQuantity === null) return;

    const next = Math.max(0, Math.round((current.stockQuantity + delta) * 100) / 100);
    await medicationsRepo.update(medication.id, { stockQuantity: next });
  });
}
